/**
 * 1日分のアクティビティデータ取得
 *
 * 無限スクロールのタイムライン／カレンダーで共有する。
 */

import { getUnmatchedCardTransactions } from '@/api/services/card-transaction'
import { getGarminSleep, getGarminSteps15Min } from '@/api/services/garmin'
import { getOverlandLocations, getOverlandVisits } from '@/api/services/overland'
import { getReadHistoryList } from '@/api/services/progress'
import { getReceipts } from '@/api/services/receipt'
import type { ReadHistory } from '@/api/type'
import type { CardTransaction } from '@/api/types/card-transaction'
import type { GarminSleepRaw, GarminSteps15Min } from '@/api/types/garmin'
import type { OverlandLocation, OverlandVisit } from '@/api/types/overland'
import type { ReceiptSummary } from '@/api/types/receipt'
import { generateMovementSessions, type MovementSessionWithVisits } from '@/lib/movement-session'

/** 1日分のデータ。Garmin アクティビティは一括取得したものを呼び出し側で日付ごとに振り分ける */
export type DayData = {
  date: string // YYYY-MM-DD
  readHistory: ReadHistory[]
  visits: OverlandVisit[]
  locations: OverlandLocation[]
  movementSessions: MovementSessionWithVisits[]
  sleep: GarminSleepRaw | null
  steps15Min: GarminSteps15Min[]
  receipts: ReceiptSummary[]
  /** レシートの無いカード利用（保険として表示する） */
  cardTransactions: CardTransaction[]
}

const READ_HISTORY_LIMIT = 500
/** 位置データはstoreと同じく2000件まで取得 */
const LOCATION_LIMIT = 2000

/** ビューをまたいでキャッシュを共有するためのクエリキー接頭辞 */
export const DAY_DATA_QUERY_PREFIX = 'activity-day'

export const dayDataQueryKey = (dateStr: string, hasFullAccess: boolean) =>
  [DAY_DATA_QUERY_PREFIX, dateStr, hasFullAccess] as const

type Options = {
  hasFullAccess: boolean
}

export const fetchDayData = async (dateStr: string, { hasFullAccess }: Options): Promise<DayData> => {
  const dayData: DayData = {
    date: dateStr,
    readHistory: [],
    visits: [],
    locations: [],
    movementSessions: [],
    sleep: null,
    steps15Min: [],
    receipts: [],
    cardTransactions: [],
  }

  try {
    // 読書履歴はAPIから日付指定で取得
    const historyRes = await getReadHistoryList(READ_HISTORY_LIMIT, dateStr)
    dayData.readHistory = historyRes.items

    if (hasFullAccess) {
      // 並列でデータを取得
      const [visitsRes, locationsRes, sleepRes, stepsRes, receiptsRes, cardTxRes] = await Promise.all([
        getOverlandVisits(dateStr).catch(() => []),
        getOverlandLocations(dateStr, LOCATION_LIMIT).catch(() => []),
        getGarminSleep(dateStr).catch(() => null),
        getGarminSteps15Min(dateStr).catch(() => []),
        getReceipts({ purchased_date: dateStr }).catch(() => []),
        getUnmatchedCardTransactions(dateStr).catch(() => []),
      ])

      dayData.visits = visitsRes
      dayData.locations = locationsRes
      dayData.sleep = sleepRes
      dayData.steps15Min = stepsRes
      dayData.receipts = receiptsRes
      dayData.cardTransactions = cardTxRes
      dayData.movementSessions = generateMovementSessions(locationsRes, visitsRes)
    }
  } catch {
    // データ取得失敗は空データで返す（ユーザーには空のタイムラインとして表示）
  }

  return dayData
}
